import { useCallback, useEffect, useState } from 'react';
import { ArrowLeftOutlined, UploadOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Row,
  Select,
  Typography,
  Upload
} from 'antd';
import { FoodManagementNutrientDetails } from '@/components/forms/food-management/nutrients.form';
import { FoodQuantificationTable } from '@/components/tables/food-management/food-management-quantification.table';
import {
  createFood,
  getListFood,
  getDetailFood,
  getListFoodQuantitative,
  getListMaterialFood,
  getListGroupFood,
  watchListFoodQuantitative,
  watchListFood,
  watchDetailFoodById,
  getFoodClassification,
  watchListGroupFood,
  watchListGroupMaterialFood,
  watchListFoodClassification,
  resetMessages,
  watchFoodLoading,
  watchFoodMessage,
  updateFood
} from '@/redux/slices/food.slice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { PageURLs } from '@/utils/navigate';
import { useQuery } from '@/hooks/query.hook';
import { setMessages } from '@/redux/slices/app.slice';
import { PermissionMode } from '@/enums/mode.enum';
import { DEFAULT_PAGINATION_PARAMS } from '@/constants/pagination.constant';
import { YesNo } from '@/enums/yes-no.enum';
import { handlePositiveNumberInputKeyDown } from '@/utils/common';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';

const FoodManagementCreateForm = () => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const listFood = useAppSelector(watchListFood);
  const listGroupFood = useAppSelector(watchListGroupFood);
  const listNutritionGroup = useAppSelector(watchListGroupMaterialFood);
  const listFoodClassification = useAppSelector(watchListFoodClassification);
  const listQuantitative = useAppSelector(watchListFoodQuantitative);
  const loading = useAppSelector(watchFoodLoading);
  const [inputNameFood, setInputNameFood] = useState('');
  const [listFoodQuantitative, setListFoodQuantitative] = useState<FoodQuantitative[]>([]);
  const detailFood = useAppSelector(watchDetailFoodById);
  const message = useAppSelector(watchFoodMessage);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [btnIdFood, setBtnIdFood] = useState(false);

  const { push, mode, foodId } = useQuery();

  useEffect(() => {
    if (detailFood?.IMAGE && mode === "edit") {
      setFileList([
        {
          uid: "-1",
          name: detailFood?.NAME || "image",
          status: "done",
          url: detailFood?.IMAGE,
        },
      ]);
    } else {
      setFileList([]);
    }
  }, [detailFood, mode]);

  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    const lastFile = newFileList[newFileList.length - 1];

    if (lastFile && lastFile.type) {
      const isImage = ['image/jpeg', 'image/png', 'image/webp'].includes(lastFile.type);
      if (!isImage) {
        return;
      }
    }

    setFileList(newFileList);
  };

  const nutritionGroupOptions = listNutritionGroup.map((item) => ({
    label: item.name,
    value: item.code
  }));

  const listFoodClassificationOptions = listFoodClassification.map((item) => ({
    label: item.name,
    value: item.code
  }));

  const foodGroupOptions = listGroupFood.map((item) => ({
    label: item.foodGroupName,
    value: item.name
  }));

  useEffect(() => {
    dispatch(getListGroupFood());
    dispatch(getListMaterialFood());
    if (mode === PermissionMode.CREATE) {
      dispatch(getFoodClassification());
    }
    dispatch(getListFood(DEFAULT_PAGINATION_PARAMS));
  }, [dispatch, mode]);

  useEffect(() => {
    const fetchData = async () => {
      if (mode === PermissionMode.EDIT && foodId) {
        await dispatch(getDetailFood(foodId));
        await dispatch(getListFoodQuantitative({ foodId: +foodId }));
      }
    };
    fetchData();
  }, [dispatch, foodId, mode]);

  useEffect(() => {
    if (mode === PermissionMode.EDIT && detailFood) {
      dispatch(getFoodClassification(detailFood.GROUP_FOOD));
      const newValues = {
        ...detailFood,
        FOOD_SNACK: detailFood.FOOD_SNACK === YesNo.Yes
      };
      form.setFieldsValue(newValues);
      setListFoodQuantitative(listQuantitative);
      setBtnIdFood(true);
    }
  }, [detailFood, dispatch, form, mode, listQuantitative]);

  const handleGoBack = useCallback(() => {
    push(PageURLs.ofFoodManagement());
  }, [push]);

  const ALLOWED_TYPES = [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/svg+xml'
  ];

  const beforeUpload = (file: File) => {
    const isValidType = ALLOWED_TYPES.includes(file.type);
    if (!isValidType) {
      return Upload.LIST_IGNORE;
    }

    const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
    const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      return Upload.LIST_IGNORE;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      return Upload.LIST_IGNORE;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const newFile = {
        uid: Date.now().toString(),
        name: file.name,
        url: reader.result,
        status: 'done',
      };
      form.setFieldValue('IMAGE', newFile.url);
    };
    return false;
  };

  const chooseMaterialFood = (code: string) => {
    if (code) {
      dispatch(getFoodClassification(code));
    }
  }
  const uploadImage = async (thumbnail: string, type: string) => {

    const response = await apiFetcher.post('upload-image', { thumbnail, type });
    return response.data;
  };
  const onFinish = async (values: Food) => {
    const isImageUrl = (value: string | undefined) => {
      return typeof value === 'string' && value.startsWith('http');
    };
    const quantitative = await Promise.all(
      listFoodQuantitative.map(async (item) => {
        const imageUrl = item.image ? isImageUrl(item.image)
          ? item.image
          : await uploadImage(item.image, 'food_quantitatives') : "";

        const imageCookedUrl = item.imageCooked ? isImageUrl(item.imageCooked)
          ? item.imageCooked
          : await uploadImage(item.imageCooked, 'food_quantitatives') : "";
        return {
          MASS: item.mass,
          UNIT: item.unit,
          QUANTITATIVE: item.quantitative,
          UNIT_QUANTITATIVE: item.unitQuantitative,
          IMAGE: imageUrl,
          IMAGE_COOKED: imageCookedUrl
        };
      })
    );

    values.FOOD_SNACK = values.FOOD_SNACK ? YesNo.Yes : YesNo.No;
    // const image = values.IMAGE;
    const image = values.IMAGE ? isImageUrl(values.IMAGE)
      ? values.IMAGE
      : await uploadImage(values.IMAGE, 'foods') : '';

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { IMAGE: _, ...restValues } = values;

    const body = {
      "foodInfo": restValues,
      "quantitative": quantitative,
      "image": image ?? ''
    }

    if (mode === PermissionMode.EDIT) {
      await dispatch(updateFood(body));
    } else {
      await dispatch(createFood(body));
    }
    await dispatch(getListFood());
    push(
      `${PageURLs.ofFoodManagement()}`
    );
  };

  useEffect(() => {
    if (message) {
      dispatch(
        setMessages([
          {
            type: message.type,
            message: message.message,
            description: message.description
          }
        ])
      );

      dispatch(resetMessages());
    }
  }, [dispatch, message]);

  return (
    <Form form={form} css={rootStyles} onFinish={onFinish}>
      <Row css={boxStyles} align='middle' gutter={16}>
        <Button
          type='text'
          icon={<ArrowLeftOutlined />}
          onClick={handleGoBack}
        ></Button>
        <Typography.Title level={5} style={{ marginBottom: 0 }}>
          {mode === PermissionMode.CREATE ? 'Tạo mới dữ liệu thực phẩm' : 'Cập nhật dữ liệu thực phẩm'}
        </Typography.Title>
      </Row>

      <Row css={boxStyles} align='top' gutter={16}>
        <Col span={12} css={fieldStyles}>
          <Form.Item
            name={'GROUP_MATERIAL'}
            label={'Nhóm nguyên liệu'}
            labelCol={{ span: 8 }}
            rules={[{ required: true, message: 'Vui lòng điền thông tin!' }]}
          >
            <Select
              placeholder='Chọn nhóm nguyên liệu'
              options={nutritionGroupOptions}
            />
          </Form.Item>
        </Col>

        <Col span={12} />

        <Col span={12} css={fieldStyles}>
          <Form.Item
            name={'GROUP_FOOD'}
            label={'Nhóm thực phẩm'}
            labelCol={{ span: 8 }}
            rules={[{ required: true, message: 'Vui lòng điền thông tin!' }]}
          >
            <Select
              placeholder='Chọn nhóm thực phẩm'
              options={foodGroupOptions}
              onChange={(option) => chooseMaterialFood(option)}
            />
          </Form.Item>
        </Col>
        <Col span={12} css={fieldStyles}>
          {listFoodClassification.length > 0 ? (
            <Form.Item
              name={'GROUP_NUTRITION'}
              label={'Phân loại chất đạm/bột đường/trái cây'}
              labelCol={{ span: 8 }}
            >
              <Select
                placeholder='Chọn phân loại chất đạm'
                options={listFoodClassificationOptions}
              />
            </Form.Item>
          ) : (
            <div style={{ height: '32px' }} />
          )}
        </Col>

        <Col span={12} css={fieldStyles}>
          <Form.Item
            name={'ID'}
            label={'ID thực phẩm'}
            labelCol={{ span: 8 }}
            rules={[
              { required: true, message: 'Vui lòng điền thông tin!' },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (mode === PermissionMode.EDIT) return Promise.resolve();
                  const isDuplicate = listFood.some(food => String(food.ID).trim() === value.trim());
                  return isDuplicate
                    ? Promise.reject(new Error('ID thực phẩm đã tồn tại'))
                    : Promise.resolve();
                },
              },
            ]}
          >
            <Input
              type="number"
              placeholder='Nhập ID thực phẩm'
              disabled={mode === PermissionMode.EDIT}
              onKeyDown={(e) => {
                const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab'];
                if (
                  !/[0-9]/.test(e.key) &&
                  !allowedKeys.includes(e.key)
                ) {
                  e.preventDefault();
                }
              }}
              onBlur={(e) => {
                const value = e.target.value?.trim();
                setBtnIdFood(!!value);

                if (value && listFoodQuantitative.length > 0) {
                  const updatedList = listFoodQuantitative.map(item => ({
                    ...item,
                    idDisplay: `${value}_${item.mass}${item.unit}`
                  }));
                  setListFoodQuantitative(updatedList);
                }
              }}
            />
          </Form.Item>
        </Col>
        <Col span={12} />

        <Col span={12} css={fieldStyles}>
          <Form.Item
            name={'NAME'}
            label={'Tên thực phẩm'}
            labelCol={{ span: 8 }}
            rules={[{ required: true, message: 'Vui lòng điền thông tin!' }]}
          >
            <Input
              placeholder='Nhập tên thực phẩm'
              onChange={(e) => setInputNameFood(e.target.value)}
            />
          </Form.Item>
        </Col>
        <Col span={12} />

        <Col span={12} offset={4} css={fieldStyles}>
          <Form.Item
            name={'FOOD_SNACK'}
            valuePropName="checked">
            <Checkbox>Thực phẩm cho bữa phụ</Checkbox>
          </Form.Item>
        </Col>

        <Col span={12} css={fieldStyles}>
          <Form.Item
            name={'IMAGE'}
            label={'Ảnh thumbnail'}
            labelCol={{ span: 8 }}
            getValueFromEvent={(e) => {
              const fl = Array.isArray(e) ? e : e?.fileList;

              if (!fl || fl.length === 0) return "";

              const file = fl[0];
              return file.url || file.response?.url || "";
            }}
          >
            <Upload
              beforeUpload={beforeUpload}
              listType='picture'
              accept='.png, .jpeg, .jpg'
              maxCount={1}
              fileList={fileList}
              onChange={handleChange}
              onRemove={() => {
                setFileList([]);
              }}
            >
              <Button icon={<UploadOutlined />}>
                Chọn để tải lên hình ảnh
              </Button>
            </Upload>
          </Form.Item>
        </Col>
      </Row>

      <Row css={boxStyles} align='top' gutter={16}>
        <Col span={12} css={fieldStyles}>
          <Form.Item
            name={'STANDARD_VOLUME'}
            label={'Khối lượng thực phẩm sống sạch tiêu chuẩn'}
            labelCol={{ span: 8 }}
            rules={[{ required: true, message: 'Vui lòng điền thông tin!' }]}
          >
            <Input
              suffix="g"
              inputMode="decimal"
              onKeyDown={handlePositiveNumberInputKeyDown}
            />
          </Form.Item>
        </Col>

        <Col span={12} css={fieldStyles}>
          <Form.Item
            name={'REMOVE_FOOD_WEIGHT'}
            label={<Typography>Thải bỏ</Typography>}
            labelCol={{ span: 8 }}
          >
            <Input
              placeholder='Nhập khối lượng thải bỏ (ví dụ: 10)'
              suffix='%'
              inputMode="decimal"
              onKeyDown={handlePositiveNumberInputKeyDown}
            />
          </Form.Item>
        </Col>

        <Col span={24} css={fieldStyles}>
          <Typography>
            Thông tin dinh dưỡng của thực phẩm chứa trong 100g thực phẩm
          </Typography>
        </Col>

        <Col span={24} css={fieldStyles}>
          <FoodManagementNutrientDetails />
        </Col>
      </Row>

      <Row css={boxStyles} align='top' gutter={16}>
        <FoodQuantificationTable
          nameFood={inputNameFood}
          setListFoodQuantitative={setListFoodQuantitative}
          listFoodQuantitative={listFoodQuantitative}
          getFoodId={() => form.getFieldValue('ID')}
          btnIdFood={btnIdFood}
        />
      </Row>

      <Row css={groupButtonStyles} align='top' gutter={16}>
        <Button type='default' size='large' onClick={handleGoBack}>
          Hủy
        </Button>

        <Form.Item noStyle>
          <Button type='primary' size='large' htmlType='submit' loading={loading}>
            Lưu
          </Button>
        </Form.Item>
      </Row>
    </Form>
  );
};
export default FoodManagementCreateForm;
const rootStyles = css`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0 1rem;
  min-height: calc(100vh - var(--header-height));
`;

const boxStyles = css`
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }
`;

const fieldStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;

  .ant-form-item {
    width: 100%;
    margin-bottom: 6px;

    .ant-form-item-row {
      display: flex;
      justify-content: space-between;
    }

    .ant-form-item-label {
      text-align: start !important;

      label {
        white-space: normal;
        height: auto;
        line-height: 1.5;
        font-weight: bold;
      }
    }
  }

  .ant-typography {
    font-weight: bold;
  }

  input {
    flex: 1;
  }
`;

const groupButtonStyles = css`
  padding: 1rem;
  background: var(--white-color);
  border-radius: 1rem;
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 1.6rem 1rem;
  }

  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  gap: 1.6rem;

  button {
    padding: 1rem 10rem;
  }
`;
